import { useMemo, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNetworkData } from '../../hooks/network/useNetworkData';
import { useQuery } from '@tanstack/react-query';
import SetupPageLayout from '../../components/setup/SetupPageLayout';
import StainDefinitionWizard from '../../components/stain/StainDefinitionWizard';
import EditOrCreateModal from '../../components/setup/EditOrCreateModal';
import api from '../../API';
import {
    DATA_MANAGER_HOME,
    DATA_MANAGER_WORKSPACE_LABEL,
    REGION_SETUP_ROUTE,
    getDataManagerStep,
} from '../../constants/setupRoadmapSteps';
import styles from './StainSetup.module.css';

const stainStep = getDataManagerStep('stain');

function StainSetup() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { local, isLoading } = useNetworkData();
    
    const wizardType = searchParams.get('wizard'); // 'stain' or null
    const editId = searchParams.get('editId'); // ID of item being edited, or null

    // Modal state for edit/create choice
    const [modalState, setModalState] = useState({
        isOpen: false,
        schemaKey: null,
        schemaTitle: null,
    });

    // Fetch existing stains
    const { data: stainsResponse, refetch: refetchStains } = useQuery({
        queryKey: ['bdsa-stain'],
        queryFn: () => api.get('/bdsa/stain'),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch stain schema
    const { data: schemasResponse } = useQuery({
        queryKey: ['bdsa-schema', 'stain'],
        queryFn: () => api.get('/bdsa/schema', { query: { name: 'stain', includeDrafts: false } }),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    // Compute data arrays (safe to compute even if responses aren't ready)
    const stains = stainsResponse?.status === 200 && Array.isArray(stainsResponse.data)
        ? stainsResponse.data
        : [];

    // Get latest schema and available stains
    const latestSchema = useMemo(() => {
        if (schemasResponse?.status === 200 && Array.isArray(schemasResponse.data) && schemasResponse.data.length > 0) {
            return schemasResponse.data.sort((a, b) => (b.version || 0) - (a.version || 0))[0];
        }
        return null;
    }, [schemasResponse]);

    const availableStains = useMemo(() => {
        if (!latestSchema) return [];
        const stainProperties = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties || {};
        return Object.entries(stainProperties).map(([key, value]) => ({
            key,
            title: value.title || key,
            abbreviation: value.abbreviation || '',
            stainType: value.stainType || '',
            recommended: value.recommended === true,
        }));
    }, [latestSchema]);

    // Group stains by recommended status
    const recommendedStains = useMemo(() => {
        return availableStains.filter(stain => stain.recommended);
    }, [availableStains]);

    const additionalStains = useMemo(() => {
        return availableStains.filter(stain => !stain.recommended);
    }, [availableStains]);

    // Helper function to count definitions for a schema key
    const getDefinitionCountForSchemaKey = useCallback((schemaKey) => {
        if (!stains.length) return 0;
        return stains.filter(stain => 
            stain.schemaStainKey === schemaKey
        ).length;
    }, [stains]);

    // Helper function to get definitions for a schema key
    const getDefinitionsForSchemaKey = useCallback((schemaKey) => {
        if (!stains.length) return [];
        return stains.filter(stain => 
            stain.schemaStainKey === schemaKey
        );
    }, [stains]);

    // Stable navigation functions - must be before any early returns
    const openWizard = useCallback((itemId = null) => {
        const params = new URLSearchParams();
        params.set('wizard', 'stain');
        params.set('wizardStep', '1');
        if (itemId) {
            params.set('editId', itemId);
        }
        navigate(`/manage/stain?${params.toString()}`);
    }, [navigate]);

    const openWizardWithSchemaKey = useCallback((schemaKey) => {
        const params = new URLSearchParams();
        params.set('wizard', 'stain');
        params.set('wizardStep', '2');
        params.set('schemaKey', schemaKey);
        navigate(`/manage/stain?${params.toString()}`);
    }, [navigate]);

    // Handle schema pill click - show modal if definitions exist, otherwise create new
    const handleSchemaPillClick = useCallback((schemaKey, schemaTitle) => {
        const existingDefinitions = getDefinitionsForSchemaKey(schemaKey);
        if (existingDefinitions.length > 0) {
            setModalState({
                isOpen: true,
                schemaKey,
                schemaTitle,
            });
        } else {
            openWizardWithSchemaKey(schemaKey);
        }
    }, [getDefinitionsForSchemaKey, openWizardWithSchemaKey]);

    const closeWizard = useCallback(() => {
        navigate('/manage/stain');
    }, [navigate]);

    // Determine editing item from URL - must be before any early returns
    const editingItemFromUrl = useMemo(() => {
        if (!wizardType || wizardType !== 'stain' || !editId) return null;
        return stains.find(s => s._id === editId);
    }, [wizardType, editId, stains]);

    // Redirect to initial config if not configured
    if (!isLoading && !local) {
        return <div><h2>Server not configured</h2><p>Ask an administrator to complete initial server setup.</p></div>;
    }

    if (isLoading) {
        return <div>Loading...</div>;
    }

    const hasStains = stains.length > 0;

    const handleStainWizardComplete = () => {
        closeWizard();
        refetchStains();
    };

    // If wizard is open, show it
    if (wizardType === 'stain') {
        return (
            <StainDefinitionWizard
                initialItem={editingItemFromUrl}
                onComplete={handleStainWizardComplete}
                onCancel={closeWizard}
            />
        );
    }

    return (
        <SetupPageLayout
            stepNumber={stainStep.number}
            totalSteps={stainStep.total}
            title={stainStep.title}
            description="Define stains for BDSA metadata. Create at least one stain definition to continue."
            backLink={DATA_MANAGER_HOME}
            backLinkText={DATA_MANAGER_WORKSPACE_LABEL}
            onNext={hasStains ? () => navigate(REGION_SETUP_ROUTE) : undefined}
            onPrevious={() => navigate(DATA_MANAGER_HOME)}
            nextDisabled={!hasStains}
            nextLabel={hasStains ? "Next: Region Definitions" : "Create at least one stain to continue"}
            previousLabel={DATA_MANAGER_WORKSPACE_LABEL}
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <h3>Stain Definitions</h3>
                </div>
                {stains.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No stain definitions found.</p>
                        <p>Create at least one stain definition to continue.</p>
                        <button
                            onClick={() => openWizard()}
                            className={styles.createButton}
                        >
                            Create First Stain
                        </button>
                    </div>
                ) : (
                    <div className={styles.list}>
                        <p className={styles.count}>{stains.length} stain{stains.length !== 1 ? 's' : ''} created</p>
                        <div className={styles.itemsList}>
                            {stains.map(stain => (
                                <div 
                                    key={stain._id} 
                                    className={styles.item}
                                    onClick={() => openWizard(stain._id)}
                                    title="Click to edit"
                                >
                                    {stain.name}
                                </div>
                            ))}
                            <div 
                                className={`${styles.item} ${styles.addItem}`}
                                onClick={() => openWizard()}
                                title="Click to create a new stain definition"
                            >
                                + Add New Stain
                            </div>
                        </div>
                    </div>
                )}

                {/* Schema Options Section */}
                {latestSchema && availableStains.length > 0 && (
                    <div className={styles.schemaOptions}>
                        <h4>Available Schema Options</h4>
                        
                        {/* Recommended Section */}
                        {recommendedStains.length > 0 && (
                            <div className={styles.recommendedSection}>
                                <h5 className={styles.sectionHeader}>Recommended</h5>
                                <div className={styles.schemaPillsList}>
                                    {recommendedStains.map(stain => {
                                        const count = getDefinitionCountForSchemaKey(stain.key);
                                        return (
                                            <div
                                                key={stain.key}
                                                className={styles.schemaPill}
                                                onClick={() => handleSchemaPillClick(stain.key, stain.title)}
                                                title={`Click to ${count > 0 ? 'edit existing or create a new' : 'create a'} definition for ${stain.title}`}
                                            >
                                                <div className={styles.schemaPillHeader}>
                                                    <span className={styles.schemaPillName}>{stain.title}</span>
                                                    {stain.abbreviation && (
                                                        <span className={styles.schemaPillAbbr}>({stain.abbreviation})</span>
                                                    )}
                                                    {stain.recommended && (
                                                        <span className={styles.recommendedBadge}>Recommended</span>
                                                    )}
                                                    {count > 0 && (
                                                        <span className={styles.schemaPillCount}>
                                                            <span className={styles.checkmark}>✓</span> {count}
                                                        </span>
                                                    )}
                                                </div>
                                                {stain.stainType && (
                                                    <span className={styles.schemaPillType}>{stain.stainType}</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Additional Section */}
                        {additionalStains.length > 0 && (
                            <div className={styles.additionalSection}>
                                <h5 className={styles.sectionHeader}>Additional</h5>
                                <div className={styles.schemaPillsList}>
                                    {additionalStains.map(stain => {
                                        const count = getDefinitionCountForSchemaKey(stain.key);
                                        return (
                                            <div
                                                key={stain.key}
                                                className={styles.schemaPill}
                                                onClick={() => handleSchemaPillClick(stain.key, stain.title)}
                                                title={`Click to ${count > 0 ? 'edit existing or create a new' : 'create a'} definition for ${stain.title}`}
                                            >
                                                <div className={styles.schemaPillHeader}>
                                                    <span className={styles.schemaPillName}>{stain.title}</span>
                                                    {stain.abbreviation && (
                                                        <span className={styles.schemaPillAbbr}>({stain.abbreviation})</span>
                                                    )}
                                                    {count > 0 && (
                                                        <span className={styles.schemaPillCount}>
                                                            <span className={styles.checkmark}>✓</span> {count}
                                                        </span>
                                                    )}
                                                </div>
                                                {stain.stainType && (
                                                    <span className={styles.schemaPillType}>{stain.stainType}</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Edit/Create Modal */}
                <EditOrCreateModal
                    isOpen={modalState.isOpen}
                    onClose={() => setModalState({ isOpen: false, schemaKey: null, schemaTitle: null })}
                    type="stain"
                    schemaTitle={modalState.schemaTitle || ''}
                    existingDefinitions={modalState.schemaKey ? getDefinitionsForSchemaKey(modalState.schemaKey) : []}
                    onEdit={(definitionId) => {
                        openWizard(definitionId);
                    }}
                    onCreate={() => {
                        if (modalState.schemaKey) {
                            openWizardWithSchemaKey(modalState.schemaKey);
                        }
                    }}
                />
            </div>
        </SetupPageLayout>
    );
}

export default StainSetup;
